<script setup>
import {computed, watch, defineModel, defineProps, defineEmits} from 'vue'
import {PAYMENT_TYPE_GROUPS, getPaymentTypeMeta} from '@shared-components/finance/paymentTypeMeta'
import {sortPaymentMethods} from './paymentMethodPresentation'

const modelValue = defineModel({default: undefined})
const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})
const emit = defineEmits(['change'])

const paymentMethods = computed(() => {
  const withMeta = props.options
    .map(option => ({...option, meta: getPaymentTypeMeta(option.value)}))
    .filter(option => option.meta)

  return sortPaymentMethods(withMeta)
})

const paymentMethodGroups = computed(() => {
  return PAYMENT_TYPE_GROUPS.map(group => ({
    ...group,
    methods: paymentMethods.value.filter(method => method.meta.group === group.key),
  })).filter(group => group.methods.length)
})

const groupedPaymentMethods = computed(() => paymentMethodGroups.value.flatMap(group => group.methods))

const selectPaymentMethod = value => {
  if (props.disabled || modelValue.value === value) {
    return
  }

  modelValue.value = value
  emit('change', value)
}

const selectFirstMethod = () => {
  const [firstMethod] = groupedPaymentMethods.value

  if (!firstMethod || modelValue.value) {
    return
  }

  selectPaymentMethod(firstMethod.value)
}

watch(groupedPaymentMethods, selectFirstMethod, {immediate: true})
</script>

<template>
  <div class="payment-method-list" :class="{'payment-method-list--disabled': disabled}">
    <div v-for="group in paymentMethodGroups" :key="group.key" class="payment-method-group">
      <p class="payment-method-group__title">{{ group.label }}</p>
      <div class="payment-method-group__grid">
        <button
          v-for="method in group.methods"
          :key="method.value"
          type="button"
          class="payment-method-card"
          :class="{'payment-method-card--active': modelValue === method.value}"
          :disabled="disabled"
          @click="selectPaymentMethod(method.value)"
        >
          <img :src="method.meta.logo" :alt="method.meta.brand" class="payment-method-card__logo" />
          <span v-if="method.meta.fee" v-tooltip.top="method.meta.fee.tooltip" class="payment-method-card__fee">
            {{ method.meta.fee.label }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.payment-method-list {
  display: flex;
  flex-direction: column;
  gap: toRem(20);
}

.payment-method-group__title {
  margin: 0 0 toRem(8);
  font-size: toRem(13);
  font-weight: 600;
  color: $text-muted;
}

.payment-method-group__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: toRem(12);
}

.payment-method-list--disabled {
  opacity: 0.55;
}

.payment-method-card {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: toRem(8);
  width: 100%;
  height: toRem(48);
  padding: toRem(8) toRem(16);
  background: #fff;
  border: toRem(1) solid $secondary-grey;
  border-radius: toRem(10);
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover:not(:disabled) {
    border-color: #d1d5db;
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: #9ca3af;
  }

  &--active {
    border-color: #9ca3af;
    background: #f3f4f6;
    box-shadow: inset 0 0 0 toRem(1) #9ca3af;
  }

  &:disabled {
    cursor: not-allowed;
  }
}

.payment-method-card__fee {
  position: absolute;
  top: toRem(4);
  right: toRem(6);
  padding: 0 toRem(6);
  background: #f3f4f6;
  border-radius: toRem(10);
  font-size: toRem(11);
  font-weight: 600;
  line-height: 1.6;
  color: $text-dark;
}

.payment-method-card__logo {
  max-width: 100%;
  max-height: toRem(28);
  object-fit: contain;
}
</style>
